<template>
    <div class="d-flex align-center">
        <v-progress-circular v-if="loading" :value="progress" :size="54" :width="4"
            :rotate="-90" color="primary" class="align-self-center ml-4 dashboard-btn"
            :title="progressLabel" :aria-label="progressLabel">
            <span style="font-size: 12px; font-weight: 600; white-space: nowrap;">{{ Math.floor(progress) }}%</span>
        </v-progress-circular>
        <v-tooltip bottom v-else>
            <template v-slot:activator="{ on, attrs }">
                <v-icon color="primary" class="align-self-center ml-4 dashboard-btn"
                    :id="idName" @click.stop="click" size="36" v-bind="attrs" v-on="on">file_upload</v-icon>
            </template>
            <span>{{ pendingExport ? "Reprendre le suivi de l'export" : "Préparer un export côté serveur" }}</span>
        </v-tooltip>
        <a v-if="readyDownload" :href="readyDownload.download_url" :download="readyDownload.filename"
            class="ml-2" @click.stop>Télécharger le fichier</a>
    </div>
</template>

<script>
import { buildVisualizationDownloadQuery } from "../elastic/queries";
import { FIELDS_SPECIAL } from "../const/fields";
import { getSitePromise } from "../vuex/modules/site";

export default {
    props: {
        site: { type: Object },
        clientSites: { type: Array },
        childIdsToName: Object,

        // mime type [xls, csv]
        type: {
            type: String,
            default: "csv",
        },
        // this prop is used to fix the problem with other components that use the
        // variable fields, like vee-validate. exportFields works exactly like fields
        exportFields: {
            type: Object,
            default: () => null,
        },
        // Use as fallback when the row has no field values
        defaultValue: {
            type: String,
            required: false,
            default: "",
        },
        average: {
            type: Boolean,
            required: false,
            default: true,
        },
        // Title(s) for the data, could be a string or an array of strings (multiple titles)
        header: {
            default: null,
        },
        // Footer(s) for the data, could be a string or an array of strings (multiple footers)
        footer: {
            default: null,
        },
        fetch: {
            type: Function,
        },
        meta: {
            type: Array,
            default: () => [],
        },
        worksheet: {
            type: String,
            default: "Sheet1",
        },
        // Determine if CSV Data should be escaped
        escapeCsv: {
            type: Boolean,
            default: true,
        },
        // long number stringify
        stringifyLongNum: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        idName() { return "export_" + this.site.id; },
        downloadFields() { return this.fields || this.exportFields || {}; },
        progressLabel() {
            if (this.progressPhase === "queued") return "Export en attente du serveur — 0 %";
            if (this.progressPhase === "finalizing") return "Mesures écrites à 100 % — finalisation du fichier serveur";
            if (this.progressPhase === "ready") return "Fichier prêt à télécharger — 100 %";
            if (this.progressTotal === null) return "Recherche des mesures — 0 %";
            return `Préparation du fichier : ${this.progressLoaded.toLocaleString()} / ${this.progressTotal.toLocaleString()} mesures — ${Math.floor(this.progress)} %`;
        },
    },
    data() {
        return {
            name: "export.csv", fields: null, loading: false, progress: 0,
            progressLoaded: 0, progressTotal: null, progressPhase: "queued", readyDownload: null, pendingExport: null,
            children_site_ids: Object.keys(this.childIdsToName || {}),
            elastic_winery_mesures_index: "winery_mesures_" + this.site.client_id,
        };
    },
    mounted() {
        this._fieldsReady = this.getFields();
        // Le clic attend la même promesse et remonte l'erreur si les champs sont indisponibles.
        this._fieldsReady.then(fields => { this.fields = fields; }).catch(() => {});
        this.pendingExport = this.readSavedExport();
        if (this.pendingExport) this.click();
    },
    beforeDestroy() {
        this._exportDisposed = true;
        if (this._exportAbort) this._exportAbort.abort();
        // Le job serveur continue, seule l'écoute locale se ferme.
    },
    methods: {
        exportStorageKey() {
            const profile = this.$store.state.profile;
            const userId = profile && profile.user ? profile.user.id : "anonymous";
            return `elastic-export:${userId}:${this.site.id}`;
        },
        readSavedExport() {
            try {
                const saved = JSON.parse(sessionStorage.getItem(this.exportStorageKey()));
                if (saved && typeof saved.progress_url === "string" && saved.expires_at * 1000 > Date.now()) {
                    const url = new URL(saved.progress_url, window.location.href);
                    if (window.location.protocol === "https:" && url.protocol !== "https:") {
                        // Cet ancien lien a été signé en HTTP. Un nouveau clic crée un lien correct.
                        sessionStorage.removeItem(this.exportStorageKey());
                        return null;
                    }
                    return saved;
                }
            } catch (error) { /* Stockage local indisponible : l'export fonctionne quand même. */ }
            return null;
        },
        saveExport(value) {
            try {
                if (value) sessionStorage.setItem(this.exportStorageKey(), JSON.stringify(value));
                else sessionStorage.removeItem(this.exportStorageKey());
            } catch (error) { /* Aucun fichier ni aucune mesure ne sont enregistrés dans le navigateur. */ }
        },
        updateProgress({ state, loaded, total }) {
            this.progressPhase = state;
            this.progressLoaded = loaded;
            this.progressTotal = total;
            this.progress = total === null ? 0 : total > 0 ? (loaded / total) * 100 : 100;
        },
        showErr(err) { this.$store.commit("setSnack", { type: "red", err }); },
        async getFields() {
            const fields = {};
            for (const sensor of this.site.sensors || []) fields[sensor.name] = sensor.csv_column;
            for (const child of Object.keys(this.childIdsToName || {})) {
                const childSite = await this.getSite(child);
                if (childSite) for (const sensor of childSite.sensors || []) fields[sensor.name] = sensor.csv_column;
            }
            fields.DateTime = "@timestamp";
            fields.Site = "Site";
            fields.events = "events";
            return fields;
        },
        async getSite(childId) {
            const found = (this.clientSites || []).find(site => site.id == childId);
            if (found) return found;
            if (this.$store.state.profile.user.role === "admin") return getSitePromise(this.$store, childId);
            return null;
        },
        getExportColumns() {
            return Object.keys(this.downloadFields).map(label => {
                const mapping = this.downloadFields[label];
                const field = typeof mapping === "string" ? mapping : mapping && mapping.field;
                if (typeof field !== "string" || !field || (mapping && typeof mapping.callback === "function")) {
                    throw new Error("L'export serveur nécessite des champs nommés, sans callback JavaScript.");
                }
                return { label, field };
            });
        },
        async click() {
            if (this.loading) return;
            this.loading = true;
            this.progress = 0;
            this.progressLoaded = 0;
            this.progressTotal = null;
            this.progressPhase = "queued";
            this.readyDownload = null;
            this._exportAbort = new AbortController();
            try {
                if (!this.pendingExport && this._fieldsReady) this.fields = await this._fieldsReady;
                if (this._exportDisposed) return;
                const format = this.type === "csv" ? "csv" : this.type === "html" ? "html" : "xlsx";
                this.name = "data_" + this.site.name + "_" + new Date().toISOString().replace(/[:.]/g, "-") + "." + format;
                const progressOptions = {
                    signal: this._exportAbort.signal,
                    onProgress: status => this.updateProgress(status),
                };
                const result = this.pendingExport
                    ? await this.$store.dispatch("elasticExportProgressRequest", {
                        ...progressOptions, progress_url: this.pendingExport.progress_url,
                    })
                    : await this.$store.dispatch("elasticDownloadRequest", {
                    ...progressOptions,
                    server_export: true, site: this.site.id, index: this.elastic_winery_mesures_index,
                    body: buildVisualizationDownloadQuery(this.site.id, this.children_site_ids, "desc"),
                    export: {
                        format, filename: this.name, columns: this.getExportColumns(),
                        default_value: this.defaultValue, average: this.average, escape_csv: this.escapeCsv,
                        worksheet: this.worksheet, special_fields: Array.isArray(FIELDS_SPECIAL) ? FIELDS_SPECIAL : [],
                        header: this.header || this.$attrs.title || null, footer: this.footer,
                    },
                    onStarted: metadata => {
                        this.pendingExport = metadata;
                        this.saveExport(metadata);
                    },
                });
                this.readyDownload = result.data;
                this.pendingExport = null;
                this.progress = 100;
                this.progressPhase = "ready";
            } catch (err) {
                if (err && err.exportState === "failed") { this.pendingExport = null; this.saveExport(null); }
                if (!this._exportDisposed) this.showErr(err);
            } finally {
                this.loading = false;
                this._exportAbort = null;
            }
        },
    },
};
</script>
